import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { STAGES } from "@/lib/finance";

export default function NewDeal() {
  const nav = useNavigate();
  const [listings, setListings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", listing_id: "", lead_id: "",
    client_name: "", client_contact: "",
    transaction_type: "sale", category: "",
    price: "", currency: "USD",
    stage: "nouveau", estimated_close_date: "",
    notes: "",
  });

  useEffect(() => {
    api.get("/listings", { params: { status: "", limit: 500 } }).then((r) => setListings(r.data));
    api.get("/leads").then((r) => setLeads(r.data));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickListing = (id) => {
    const l = listings.find((x) => x.id === id);
    if (l) {
      setForm((f) => ({
        ...f,
        listing_id: id,
        price: f.price || l.price || "",
        currency: l.currency || "USD",
        category: l.category || f.category,
        transaction_type: l.purpose === "rent" ? "rent" : "sale",
        name: f.name || l.title,
      }));
    }
  };

  const onPickLead = (id) => {
    const l = leads.find((x) => x.id === id);
    if (l) {
      setForm((f) => ({ ...f, lead_id: id, client_name: f.client_name || l.name, client_contact: f.client_contact || l.phone }));
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nom de la transaction requis");
    const price = Number(form.price);
    if (price < 0) return toast.error("Prix invalide");
    setSaving(true);
    try {
      const payload = {
        ...form,
        price,
        listing_id: form.listing_id || null,
        lead_id: form.lead_id || null,
      };
      const r = await api.post("/deals", payload);
      toast.success("Transaction créée");
      nav(`/admin/deals/${r.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="new-deal">
      <div className="flex items-center gap-3">
        <button onClick={() => nav(-1)} className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-brand-orange font-semibold">Nouvelle transaction</div>
          <h1 className="mt-1 font-display text-3xl text-brand-navy tracking-tight">Créer une transaction</h1>
        </div>
      </div>

      <Card className="p-6 lg:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="lg:col-span-2">
            <Label>Nom de la transaction *</Label>
            <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Ex : Villa Golf pour M. Kabila" data-testid="deal-name" />
          </div>

          <div>
            <Label>Offre concernée</Label>
            <Select value={form.listing_id || "none"} onValueChange={(v) => v === "none" ? update("listing_id", "") : onPickListing(v)}>
              <SelectTrigger data-testid="deal-listing"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucune</SelectItem>
                {listings.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.title} — {l.city}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Client (prospect CRM)</Label>
            <Select value={form.lead_id || "none"} onValueChange={(v) => v === "none" ? update("lead_id", "") : onPickLead(v)}>
              <SelectTrigger data-testid="deal-lead"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun (client hors CRM)</SelectItem>
                {leads.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.name} — {l.phone || l.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div><Label>Nom client (affichage)</Label><Input value={form.client_name} onChange={(e) => update("client_name", e.target.value)} /></div>
          <div><Label>Contact client</Label><Input value={form.client_contact} onChange={(e) => update("client_contact", e.target.value)} placeholder="Téléphone / email" /></div>

          <div>
            <Label>Type de transaction</Label>
            <Select value={form.transaction_type} onValueChange={(v) => update("transaction_type", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sale">Vente</SelectItem>
                <SelectItem value="rent">Location</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div><Label>Catégorie</Label><Input value={form.category} onChange={(e) => update("category", e.target.value)} placeholder="villa, suv, camion…" /></div>

          <div>
            <Label>Prix du bien *</Label>
            <Input type="number" step="0.01" value={form.price} onChange={(e) => update("price", e.target.value)} data-testid="deal-price" />
          </div>
          <div>
            <Label>Devise</Label>
            <Select value={form.currency} onValueChange={(v) => update("currency", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="CDF">CDF</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Statut CRM</Label>
            <Select value={form.stage} onValueChange={(v) => update("stage", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Date de clôture estimée</Label>
            <Input type="date" value={form.estimated_close_date} onChange={(e) => update("estimated_close_date", e.target.value)} />
          </div>

          <div className="lg:col-span-2">
            <Label>Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)} />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={() => nav(-1)}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-brand-orange hover:bg-brand-orangeDark text-white" data-testid="deal-save">
            {saving ? "Création…" : "Créer la transaction"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
